import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { IframeManager } from '@/lib/canvas/shader/iframe-manager'
import { SHADER_INPUT_LABELS, SHADER_INPUT_TYPES } from '@/lib/canvas/shader/media/contract'
import { ShaderInputSet } from '@/lib/canvas/shader/media/input-set'
import type { RuntimeMode, ShaderError } from '@/lib/canvas/shader/types'
import type { ShaderInputSources, ShaderInputType } from '@/lib/canvas/shader/media/contract'
import type { ShaderInputSnapshot, ShaderInputStatus } from '@/lib/canvas/shader/media/input-set'
import type { MediaSize } from '@/lib/canvas/shader/media/input-sources'
import type { RefObject } from 'react'

const DEFAULT_DIMS = { w: 512, h: 288 }
const MAX_SIDE = 512
const CAPTURE_FEEDBACK_MS = 1200
const EMPTY_SNAPSHOT: ShaderInputSnapshot = { status: {}, errors: {}, sizes: {}, audioNeedsGesture: false }

export interface ShaderPreviewOptions {
  code: string
  runtimeMode: RuntimeMode
  paused: boolean
  paramValues: Record<string, unknown>
  /** Media from connected upstream nodes. */
  inputs?: ShaderInputSources
  onCapture: (dataUrl: string) => void
}

export interface ShaderPreviewAudio {
  /** An audio input is connected and playing through the analyser. */
  connected: boolean
  /** The browser is holding playback until the user clicks (autoplay policy). */
  needsGesture: boolean
  muted: boolean
  setMuted: (muted: boolean) => void
  /** Start held playback. Call from a click handler. */
  resume: () => void
}

export interface ShaderPreview {
  containerRef: RefObject<HTMLDivElement | null>
  /** Canvas size: the image's aspect when one is connected, else the video's, else 16:9. */
  dims: { w: number; h: number }
  ready: boolean
  /** Runtime error first (compile, runtime, unresponsive, context lost), then the first input error. */
  error: ShaderError | null
  /** True while any input is loading. */
  inputPreparing: boolean
  inputStatus: Partial<Record<ShaderInputType, ShaderInputStatus>>
  justCaptured: boolean
  restart: () => void
  capture: () => void
  audio: ShaderPreviewAudio
}

/**
 * Runs a shader's code in its sandboxed iframe and keeps the iframe in step
 * with the node: code, pause, params and every connected input are re-sent
 * whenever the iframe (re)loads, so reloads, restarts and resizes never drop state.
 */
export function useShaderPreview({ code, runtimeMode, paused, paramValues, inputs, onCapture }: ShaderPreviewOptions): ShaderPreview {
  const containerRef = useRef<HTMLDivElement>(null)
  const managerRef = useRef<IframeManager | null>(null)
  const iframeReadyRef = useRef(false)
  const loadedRef = useRef<{ code: string; mode: RuntimeMode } | null>(null)
  const latest = useRef({ code, runtimeMode, paused, paramValues, onCapture })
  const captureTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  const [dims, setDims] = useState(DEFAULT_DIMS)
  const [ready, setReady] = useState(false)
  const [runtimeError, setRuntimeError] = useState<ShaderError | null>(null)
  const [snapshot, setSnapshot] = useState<ShaderInputSnapshot>(EMPTY_SNAPSHOT)
  const [muted, setMutedState] = useState(false)
  const [justCaptured, setJustCaptured] = useState(false)

  // Inputs load outside React; the set reports back through this listener.
  const [inputSet] = useState(() => new ShaderInputSet({
    onChange: setSnapshot,
    onLoaded: (type, message) => {
      if (iframeReadyRef.current) managerRef.current?.sendInput(type, message.data, message.transfer)
    },
    onCleared: (type) => managerRef.current?.sendInput(type, null),
    onAudioFrame: (frame) => {
      if (iframeReadyRef.current) managerRef.current?.sendInput('audio', frame)
    },
  }, { paused }))

  useEffect(() => {
    latest.current = { code, runtimeMode, paused, paramValues, onCapture }
  }, [code, runtimeMode, paused, paramValues, onCapture])

  useEffect(() => () => {
    clearTimeout(captureTimer.current)
    inputSet.dispose()
  }, [inputSet])

  const { image, video, audio, text } = inputs ?? {}
  const model = inputs?.['3d']
  useEffect(() => {
    inputSet.update({ image, video, audio, text, '3d': model })
  }, [inputSet, image, video, audio, text, model])

  // Match the canvas to the image's aspect ratio, else the video's (16:9 without either).
  const mediaSize = snapshot.sizes.image ?? snapshot.sizes.video
  useEffect(() => {
    const next = mediaSize ? fitDims(mediaSize) : DEFAULT_DIMS
    setDims((prev) => (prev.w === next.w && prev.h === next.h ? prev : next))
  }, [mediaSize])

  // One iframe per canvas size; it loads the current code straight away.
  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    const mgr = new IframeManager({ width: dims.w, height: dims.h })
    mgr.setCallbacks({
      onReady: () => {
        iframeReadyRef.current = true
        setReady(true)
        setRuntimeError(null)
        const { paused: isPaused, paramValues: values } = latest.current
        for (const { type, message } of inputSet.messages()) mgr.sendInput(type, message.data, message.transfer)
        sendParams(mgr, values)
        if (isPaused) mgr.sendControl('pause')
      },
      onError: setRuntimeError,
      onRecover: () => setRuntimeError((prev) => (prev?.kind === 'unresponsive' ? null : prev)),
      onCapture: (dataUrl) => {
        latest.current.onCapture(dataUrl)
        setJustCaptured(true)
        clearTimeout(captureTimer.current)
        captureTimer.current = setTimeout(() => setJustCaptured(false), CAPTURE_FEEDBACK_MS)
      },
    })
    mgr.create(container)
    managerRef.current = mgr
    iframeReadyRef.current = false
    setReady(false)
    const current = latest.current
    if (current.code.trim()) {
      mgr.loadCode(current.code, current.runtimeMode)
      loadedRef.current = { code: current.code, mode: current.runtimeMode }
    }
    return () => {
      mgr.destroy()
      managerRef.current = null
      loadedRef.current = null
      iframeReadyRef.current = false
    }
  }, [dims.w, dims.h, inputSet])

  useEffect(() => {
    const mgr = managerRef.current
    if (!mgr) return
    const loaded = loadedRef.current
    if (!code.trim()) {
      if (loaded) mgr.clear()
      loadedRef.current = null
      iframeReadyRef.current = false
      setReady(false)
      setRuntimeError(null)
      return
    }
    if (loaded && loaded.code === code && loaded.mode === runtimeMode) return
    iframeReadyRef.current = false
    setReady(false)
    setRuntimeError(null)
    mgr.loadCode(code, runtimeMode)
    loadedRef.current = { code, mode: runtimeMode }
  }, [code, runtimeMode])

  useEffect(() => {
    managerRef.current?.sendControl(paused ? 'pause' : 'resume')
    inputSet.setPaused(paused)
  }, [paused, inputSet])

  useEffect(() => {
    inputSet.setMuted(muted)
  }, [muted, inputSet])

  useEffect(() => {
    if (managerRef.current) sendParams(managerRef.current, paramValues)
  }, [paramValues])

  const restart = useCallback(() => {
    iframeReadyRef.current = false
    managerRef.current?.sendControl('restart')
    inputSet.restartAudio()
    setReady(false)
    setRuntimeError(null)
  }, [inputSet])

  const capture = useCallback(() => {
    managerRef.current?.sendControl('capture')
  }, [])

  const setMuted = useCallback((next: boolean) => setMutedState(next), [])
  const resumeAudio = useCallback(() => inputSet.resumeAudio(), [inputSet])

  const inputError = useMemo<ShaderError | null>(() => {
    const type = SHADER_INPUT_TYPES.find((t) => snapshot.errors[t])
    return type ? { kind: 'input', message: `${SHADER_INPUT_LABELS[type]} input: ${snapshot.errors[type]}` } : null
  }, [snapshot])

  const audioState = useMemo<ShaderPreviewAudio>(() => ({
    connected: snapshot.status.audio === 'ready',
    needsGesture: snapshot.audioNeedsGesture,
    muted,
    setMuted,
    resume: resumeAudio,
  }), [snapshot, muted, setMuted, resumeAudio])

  const inputPreparing = SHADER_INPUT_TYPES.some((t) => snapshot.status[t] === 'loading')

  return {
    containerRef,
    dims,
    ready,
    error: runtimeError ?? inputError,
    inputPreparing,
    inputStatus: snapshot.status,
    justCaptured,
    restart,
    capture,
    audio: audioState,
  }
}

function fitDims({ width, height }: MediaSize): { w: number; h: number } {
  const aspect = width / height
  return aspect >= 1 ? { w: MAX_SIDE, h: Math.round(MAX_SIDE / aspect) } : { w: Math.round(MAX_SIDE * aspect), h: MAX_SIDE }
}

function sendParams(mgr: IframeManager, values: Record<string, unknown>) {
  for (const [name, value] of Object.entries(values)) {
    if (typeof value === 'number' || typeof value === 'string' || typeof value === 'boolean') mgr.sendParam(name, value)
  }
}
